"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import {
  RUN_MY_FREE_AUDIT_CTA,
  SITEGLUE_PRIMARY_AUDIT_NAV,
} from "~/lib/siteglue/landing-redesign/copy";

export function RunFreeAuditNavCta({
  siteHref,
  variant = "desktop",
  onNavigate,
}: {
  siteHref: (path: string) => string;
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const linkClass =
    variant === "desktop"
      ? "block px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
      : "flex min-h-11 items-center rounded-md px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50";

  return (
    <div ref={rootRef} className={variant === "desktop" ? "relative hidden sm:block" : "w-full"}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        data-testid={variant === "desktop" ? "link-scan-my-business" : "link-mobile-scan-my-business"}
        onClick={() => setOpen((current) => !current)}
        className={
          variant === "desktop"
            ? "hidden h-9 items-center gap-1 rounded-full bg-[#4f81bd] px-4 text-sm font-semibold text-white hover:opacity-95 sm:inline-flex"
            : "flex min-h-11 w-full items-center justify-center gap-1 rounded-full bg-[#4f81bd] px-3 text-sm font-semibold text-white"
        }
      >
        {RUN_MY_FREE_AUDIT_CTA}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className={
            variant === "desktop"
              ? "absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
              : "mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white py-1"
          }
        >
          {SITEGLUE_PRIMARY_AUDIT_NAV.map((item) => (
            <Link
              key={item.href}
              role="menuitem"
              href={siteHref(item.href)}
              className={linkClass}
              onClick={() => {
                trackSiteGlueEvent("homepage_primary_cta_click", { destination: item.href });
                setOpen(false);
                onNavigate?.();
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
